import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { recoverableLazy } from '@/components/ModuleRecovery/ModuleRecovery'
import { Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useCampanhasLayout } from '@/layouts/CampanhasLayout/CampanhasLayoutContext'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchCampaignById,
  fetchPublicCampaignTemplates,
  parsePremiatedLuckyNumbersFromApi,
  type CampaignCarouselOrderToken,
  type PublicCampaignTemplateOption,
  updateCampaign,
  type Campaign,
  type RegulationContentType,
  type CampaignStatus,
  type CampaignUpsertInput,
} from '@/lib/campaigns'
import {
  sanitizeCampaignSocialLinksInput,
  type CampaignSocialLinks,
} from '@/lib/campaign-social-links'
import { CampaignImageUploadField } from '@/components/CampaignImageUploadField'
import { CampaignRichTextField } from '@/components/CampaignRichTextField'
import { CombosManager } from '@/components/CombosManager'
import { PerguntasManager } from '@/components/PerguntasManager'
import { Template3MissingContentModal } from './Template3MissingContentModal'
import { CampaignWidgetManager } from '@/components/CampaignWidgetManager/CampaignWidgetManager'
import { SensitiveSellerIdModal } from '@/components/SensitiveSellerIdModal/SensitiveSellerIdModal'
import { RoletaPanel } from './RoletaPanel'
import { ApiRoulettePanel } from '@/features/apiRoulette/ApiRoulettePanel'
import { CampanhaPremiatedNumbersTab } from './CampanhaPremiatedNumbersTab'
import { GamificacaoTab } from './GamificacaoTab'
import { PurchaseScratchTab } from './PurchaseScratchTab'
import { CampaignAiTab } from '@/features/campaignAi/AiSettings'
import {
  ContainerTabs,
  ContainerTabsList,
  ContainerTab,
  ContainerTabPanel,
} from '@/components/ContainerTabs'
import { CampaignDashboardContainer } from '@/components/CampaignDashboard'
import { CurrencyInput } from '@/components/CurrencyInput'
import {
  PIX_QR_EXPIRATION_OPTIONS,
  pixQrExpirationMinutesToFormValue,
} from '@/lib/pixQrExpiration'
import {
  hasCampaignEditChanges,
  serializeCampaignEditSnapshot,
  toCampaignEditCarouselSnapshot,
} from '@/lib/campaign-edit-snapshot'
import {
  formatCampaignMarketingApiErrors,
  hasCampaignMarketingFieldErrors,
  validateCampaignMarketingFields,
  type CampaignMarketingFieldErrors,
} from '@/lib/campaign-marketing-validation'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import { env } from '@/lib/env'
import {
  isReservedCustomDomainHost,
  normalizeCustomDomainInput,
} from '@/lib/campaign-custom-domain'
import { resolveCampaignViewPageUrl } from '@/lib/campaign-view-page-url'
import {
  getPublicCampaignTemplateDefinition,
  matchesPublicCampaignTemplate,
  PUBLIC_CAMPAIGN_TEMPLATE_DEFINITIONS,
  TEMPLATE_3_PUBLIC_CAMPAIGN_TEMPLATE_KEY,
} from '@/features/publicCampaignTemplates/registry'
import { htmlHasVisibleText } from '@/lib/landing-html'
import { campaignWhatsAppLinkForEditor } from './editor/campaignWhatsAppLink'
import { resolvePublicCampaignColors } from '@/features/publicCampaignTemplates/theme'
import {
  emptyTemplate3Advantages,
  emptyTemplate3Faq,
  listMissingTemplate3Fields,
  normalizeTemplate3Advantages,
  normalizeTemplate3Faq,
} from '@/lib/template3-campaign'
import fixedSaveStyles from '@/layouts/CampanhasLayout/CampanhasLayoutFixedSave.module.css'
import styles from './CampanhaEditPage.module.css'
import editorStyles from './editor/CampaignEditor.module.css'
import { CampaignPublicPageFields } from './editor/CampaignPublicPageFields'
import { CampaignEditorSectionHeader } from './editor/CampaignEditorSectionHeader'
import { CampaignEditorPersistentPanel } from './editor/CampaignEditorPersistentPanel'
import type { CampaignEditorSaveTask } from './editor/campaignEditorSaveTask'
import {
  CAMPAIGN_EDITOR_TABS,
  resolveCampaignEditorTab,
  getCampaignEditorGroup,
  campaignFieldTab,
} from './editor/campaignEditorNavigation'

function toDateTimeLocal(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toApiDateTime(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString()
}

function normalizeHttpsUrl(value?: string | null): string {
  const trimmed = value?.trim() ?? ''

  if (!trimmed) return ''
  if (/^https:\/\//i.test(trimmed)) return trimmed
  if (/^http:\/\//i.test(trimmed)) return trimmed.replace(/^http:\/\//i, 'https://')
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`
  if (/^[a-z]+:\/\//i.test(trimmed)) return trimmed

  return `https://${trimmed}`
}

function emptyForm(): CampaignUpsertInput {
  return {
    name: '',
    slug: '',
    title: '',
    description: '',
    prize_description: '',
    starts_at: '',
    ends_at: '',
    total_numbers: '',
    price: '',
    min_quantity_per_purchase: '',
    max_quantity_per_purchase: '',
    doey_seller_id: '',
    cta_text: '',
    status: 'draft',
    whatsapp_button_url: '',
    primary_color: '',
    secondary_color: '',
    button_color: '',
    highlight_color: '',
    youtube_video_url: '',
    main_image: null,
    regulation_pdf: null,
    regulation_content_type: 'document',
    regulation_text: '',
    certificate_pdf: null,
    login_url: '',
    public_page_url: '',
    custom_domain: '',
    public_reviews_text: '',
    public_live_badge_text: '',
    public_about_url: '',
    public_contact_url: '',
    public_social_links: {},
    public_notice_text: '',
    public_awarded_titles_html: '',
    public_details_html: '',
    public_page_title: '',
    public_share_title: '',
    public_share_description: '',
    public_template_key: 'default',
    template3_eyebrow: '',
    template3_about_title: '',
    template3_about_text: '',
    template3_advantages: emptyTemplate3Advantages(),
    template3_faq: emptyTemplate3Faq(),
    template3_hero_background: null,
    template3_about_image: null,
    template3_advantage_image_1: null,
    template3_advantage_image_2: null,
    template3_advantage_image_3: null,
    template3_hero_background_url: null,
    template3_about_image_url: null,
    template3_floating_cta_image: null,
    template3_floating_cta_image_url: null,
    public_share_image: null,
    fb_pixel: '',
    fb_access_token: '',
    g_pixel: '',
    gtm_measurement_id: '',
    ga4_measurement_protocol_secret: '',
    certificate_number: '',
    email_template_subject: '',
    email_template_body: '',
    premiated_lucky_numbers: [],
    premiated_number_assignments: undefined,
    widget_embed_button_text: '',
    widget_embed_button_color: '',
    widget_embed_button_text_color: '',
    widget_embed_button_radius_px: '',
    pix_qr_expiration_minutes: '',
  }
}

function mapCampaignToForm(campaign: Campaign): CampaignUpsertInput {
  const whatsappLink = campaignWhatsAppLinkForEditor(campaign)
  return {
    name: campaign.name ?? '',
    slug: campaign.slug ?? '',
    title: campaign.title ?? '',
    description: campaign.description ?? '',
    prize_description: campaign.prize_description ?? '',
    starts_at: toDateTimeLocal(campaign.starts_at),
    ends_at: toDateTimeLocal(campaign.ends_at),
    total_numbers: campaign.total_numbers ? String(campaign.total_numbers) : '',
    price: campaign.price ? String(campaign.price) : '',
    min_quantity_per_purchase: campaign.min_quantity_per_purchase
      ? String(campaign.min_quantity_per_purchase)
      : '',
    max_quantity_per_purchase: campaign.max_quantity_per_purchase
      ? String(campaign.max_quantity_per_purchase)
      : '',
    doey_seller_id: campaign.doey_seller_id ?? '',
    cta_text: campaign.cta_text ?? '',
    status: (campaign.status ?? 'draft') as CampaignStatus,
    whatsapp_button_url: whatsappLink,
    primary_color: campaign.primary_color ?? '',
    secondary_color: campaign.secondary_color ?? '',
    button_color: campaign.button_color ?? '',
    highlight_color: campaign.highlight_color ?? '',
    youtube_video_url: campaign.youtube_video_url ?? '',
    main_image: null,
    regulation_pdf: null,
    regulation_content_type:
      campaign.regulation_content_type === 'text' ? 'text' : 'document',
    regulation_text: campaign.regulation_text ?? '',
    certificate_pdf: null,
    login_url: campaign.login_url ?? '',
    public_page_url: campaign.public_page_url ?? '',
    custom_domain: campaign.custom_domain ?? '',
    public_reviews_text: campaign.public_reviews_text ?? '',
    public_live_badge_text: campaign.public_live_badge_text ?? '',
    public_about_url: campaign.public_about_url ?? '',
    public_contact_url: whatsappLink,
    public_social_links: sanitizeCampaignSocialLinksInput(
      (campaign.public_social_links as CampaignSocialLinks | null | undefined) ?? null
    ),
    public_notice_text: campaign.public_notice_text ?? '',
    public_awarded_titles_html: campaign.public_awarded_titles_html ?? '',
    public_details_html: campaign.public_details_html ?? '',
    public_page_title: campaign.public_page_title ?? '',
    public_share_title: campaign.public_share_title ?? '',
    public_share_description: campaign.public_share_description ?? '',
    public_template_key: campaign.public_template_key ?? 'default',
    template3_eyebrow: campaign.template3_eyebrow ?? '',
    template3_about_title: campaign.template3_about_title ?? '',
    template3_about_text: campaign.template3_about_text ?? '',
    template3_advantages: normalizeTemplate3Advantages(campaign.template3_advantages),
    template3_faq: normalizeTemplate3Faq(campaign.template3_faq),
    template3_hero_background: null,
    template3_about_image: null,
    template3_advantage_image_1: null,
    template3_advantage_image_2: null,
    template3_advantage_image_3: null,
    template3_hero_background_url: campaign.template3_hero_background_url ?? null,
    template3_about_image_url: campaign.template3_about_image_url ?? null,
    template3_floating_cta_image: null,
    template3_floating_cta_image_url: campaign.template3_floating_cta_image_url ?? null,
    public_share_image: null,
    fb_pixel: campaign.fb_pixel ?? '',
    fb_access_token: campaign.fb_access_token ?? '',
    g_pixel: campaign.g_pixel ?? '',
    gtm_measurement_id: campaign.gtm_measurement_id ?? '',
    ga4_measurement_protocol_secret: campaign.ga4_measurement_protocol_secret ?? '',
    certificate_number: campaign.certificate_number ?? '',
    email_template_subject: campaign.email_template_subject ?? '',
    email_template_body: campaign.email_template_body ?? '',
    premiated_lucky_numbers: parsePremiatedLuckyNumbersFromApi(
      campaign.premiated_lucky_numbers
    ),
    premiated_number_assignments: Array.isArray(campaign.premiated_number_assignments)
      ? campaign.premiated_number_assignments.map((row) => ({ ...row }))
      : undefined,
    widget_embed_button_text: campaign.widget_embed_button_text ?? '',
    widget_embed_button_color: campaign.widget_embed_button_color ?? '',
    widget_embed_button_text_color: campaign.widget_embed_button_text_color ?? '',
    widget_embed_button_radius_px:
      campaign.widget_embed_button_radius_px != null
        ? String(campaign.widget_embed_button_radius_px)
        : '',
    pix_qr_expiration_minutes: pixQrExpirationMinutesToFormValue(
      campaign.pix_qr_expiration_minutes
    ),
  }
}

const AvatarTab = recoverableLazy(async () => {
  const module = await import('./AvatarTab')
  return { default: module.AvatarTab }
})

const TOTAL_NUMBERS_PRESETS = [
  { value: '1000', label: '1.000' },
  { value: '10000', label: '10.000' },
  { value: '100000', label: '100.000' },
  { value: '1000000', label: '1.000.000' },
  { value: '2000000', label: '2.000.000' },
  { value: '5000000', label: '5.000.000' },
  { value: '10000000', label: '10.000.000' },
] as const

const MAX_TOTAL_NUMBERS = 100000000
const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_PDF_BYTES = 10 * 1024 * 1024
const MAX_CAROUSEL_IMAGES = 10
const CAMPAIGN_IMAGE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,image/gif'

type ExistingCarouselItem = {
  kind: 'existing'
  id: number
  imageUrl: string
}

type NewCarouselItem = {
  kind: 'new'
  key: string
  file: File
  previewUrl: string
}

type CarouselItem = ExistingCarouselItem | NewCarouselItem

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

const FALLBACK_PUBLIC_TEMPLATE_OPTIONS: PublicCampaignTemplateOption[] = Object.values(
  PUBLIC_CAMPAIGN_TEMPLATE_DEFINITIONS
).map((template) => {
  const definition = getPublicCampaignTemplateDefinition(template.key)

  return {
    key: definition.key,
    name: definition.name,
    description: definition.description,
  }
})

function validateFileSize(file: File, maxBytes: number, label: string): string | null {
  if (file.size > maxBytes) {
    return `${label} excede o limite de ${formatFileSize(maxBytes)}. Arquivo selecionado: ${formatFileSize(file.size)}.`
  }

  return null
}

const TEMPLATE_3_MAIN_IMAGE_HINT = '(Foto do prêmio no centro do topo da página pública)'
const DEFAULT_MAIN_IMAGE_HINT =
  '(Imagem de destaque exibida no topo da página da campanha)'

type CampaignMainImageFieldProps = {
  file: File | null
  imageUrl: string | null
  hint: string
  onFileChange: (file: File | null) => void
  onFileError: (message: string | null) => void
}

function CampaignMainImageField({
  file,
  imageUrl,
  hint,
  onFileChange,
  onFileError,
}: CampaignMainImageFieldProps) {
  return (
    <CampaignImageUploadField
      label="Imagem principal da campanha"
      hint={hint}
      emptyHint="PNG, JPG, WEBP ou GIF, até 5 MB. Recomendado: 1200 x 600 px"
      accept={CAMPAIGN_IMAGE_ACCEPT}
      maxBytes={MAX_IMAGE_BYTES}
      validateFileSize={validateFileSize}
      file={file}
      imageUrl={imageUrl}
      onFileChange={onFileChange}
      onFileError={onFileError}
      previewAlt="Visualização da imagem principal"
      inputId="campaign-main-image"
      validationLabel="A imagem principal"
    />
  )
}

function buildCarouselItemKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `carousel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function createNewCarouselItem(file: File): NewCarouselItem {
  return {
    kind: 'new',
    key: buildCarouselItemKey(),
    file,
    previewUrl: URL.createObjectURL(file),
  }
}

function revokeCarouselItemPreview(item: CarouselItem) {
  if (item.kind === 'new') {
    URL.revokeObjectURL(item.previewUrl)
  }
}

function revokeCarouselItemPreviews(items: CarouselItem[]) {
  items.forEach(revokeCarouselItemPreview)
}

function mapCampaignCarouselToItems(campaign: Campaign): CarouselItem[] {
  return (campaign.campaign_images ?? [])
    .filter((image) => Boolean(image.image_url))
    .map((image) => ({
      kind: 'existing' as const,
      id: image.id,
      imageUrl: image.image_url,
    }))
}

function extractApiErrorMessage(error: ApiClientError): string {
  if (error.errors) {
    const firstEntry = Object.values(error.errors).find(
      (messages) => Array.isArray(messages) && messages.length > 0
    )
    if (firstEntry?.[0]) {
      return firstEntry[0]
    }
  }

  return error.message
}

export function CampanhaEditPage() {
  const { guard, token, isCollaborator } = useAuth()
  const { runWithStepUp } = useCampaignTotpStepUp()
  const {
    setSaveTrigger,
    setIsSubmitting: setLayoutSubmitting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    reportSaveSuccess,
    setViewPageUrl,
    setActiveEditTab,
    setCampaignStatusControl,
  } = useCampanhasLayout()
  const [params] = useSearchParams()
  const campaignIdParam = params.get('id')
  const campaignId =
    campaignIdParam !== null && Number.isFinite(Number(campaignIdParam))
      ? Number(campaignIdParam)
      : null

  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = resolveCampaignEditorTab(location.hash || params.get('tab'))
  const setActiveTab = useCallback(
    (tab: string, replace = false) => {
      const nextParams = new URLSearchParams(location.search)
      nextParams.delete('tab')
      navigate(
        {
          pathname: location.pathname,
          search: nextParams.toString(),
          hash: resolveCampaignEditorTab(tab),
        },
        { replace }
      )
    },
    [location.pathname, location.search, navigate]
  )
  const activeGroup = getCampaignEditorGroup(activeTab)
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))

  useEffect(() => {
    setActiveEditTab(activeTab)
    return () => setActiveEditTab(null)
  }, [activeTab, setActiveEditTab])

  const [form, setForm] = useState<CampaignUpsertInput>(emptyForm())
  const themeColors = resolvePublicCampaignColors(form)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingAllPanels, setIsSavingAllPanels] = useState(false)
  const savingAllPanelsRef = useRef(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const [loadedCampaignId, setLoadedCampaignId] = useState<number | null>(null)
  const [avatarSave, setAvatarSave] = useState<CampaignEditorSaveTask | null>(null)
  const [aiSave, setAiSave] = useState<CampaignEditorSaveTask | null>(null)
  const [rouletteSave, setRouletteSave] = useState<CampaignEditorSaveTask | null>(null)
  const [apiRouletteSave, setApiRouletteSave] = useState<CampaignEditorSaveTask | null>(
    null
  )
  const [purchaseScratchSave, setPurchaseScratchSave] =
    useState<CampaignEditorSaveTask | null>(null)
  const [gamificationSave, setGamificationSave] = useState<CampaignEditorSaveTask | null>(
    null
  )
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([])
  const [regulationPdfPreview, setRegulationPdfPreview] = useState<string | null>(null)
  const [certificatePdfPreview, setCertificatePdfPreview] = useState<string | null>(null)
  const [existingMainImageUrl, setExistingMainImageUrl] = useState<string | null>(null)
  const [existingShareImageUrl, setExistingShareImageUrl] = useState<string | null>(null)
  const [existingRegulationPdfUrl, setExistingRegulationPdfUrl] = useState<string | null>(
    null
  )
  const [existingCertificatePdfUrl, setExistingCertificatePdfUrl] = useState<
    string | null
  >(null)
  const [publicTemplateOptions, setPublicTemplateOptions] = useState<
    PublicCampaignTemplateOption[]
  >(FALLBACK_PUBLIC_TEMPLATE_OPTIONS)

  /** Indica se a campanha já teve configuração de números ou modo de compra sem números da sorte no servidor (imutável). */
  const serverHadNumberConfigRef = useRef<boolean>(false)
  const [isDonationOnlyCampaign, setIsDonationOnlyCampaign] = useState(false)
  const [marketingFieldErrors, setMarketingFieldErrors] =
    useState<CampaignMarketingFieldErrors>({})
  const [template3MissingModalOpen, setTemplate3MissingModalOpen] = useState(false)
  const [sellerIdChangeModalOpen, setSellerIdChangeModalOpen] = useState(false)
  const savedSellerIdRef = useRef('')
  const pendingSellerChangeRef = useRef<CampaignUpsertInput | null>(null)

  const carouselImagesInputRef = useRef<HTMLInputElement>(null)
  const alertsRef = useRef<HTMLDivElement>(null)
  const carouselItemsRef = useRef<CarouselItem[]>([])

  useEffect(() => {
    carouselItemsRef.current = carouselItems
  }, [carouselItems])

  const getTotalNumbersMode = useCallback(
    (
      value: string | undefined,
      donationOnly: boolean
    ): 'preset' | 'custom' | 'donation' => {
      if (donationOnly) return 'donation'
      if (!value) return 'preset'
      return TOTAL_NUMBERS_PRESETS.some((p) => p.value === value) ? 'preset' : 'custom'
    },
    []
  )

  const [totalNumbersMode, setTotalNumbersMode] = useState<
    'preset' | 'custom' | 'donation'
  >('preset')

  useEffect(() => {
    const mode = getTotalNumbersMode(form.total_numbers, isDonationOnlyCampaign)
    setTotalNumbersMode(mode)
  }, [form.total_numbers, getTotalNumbersMode, isDonationOnlyCampaign])

  const maxPremiatedDigitsPerNumber = useMemo(() => {
    const raw = form.total_numbers?.trim() ?? ''
    const n = parseInt(raw, 10)
    if (!Number.isFinite(n) || n < 1) {
      return 32
    }
    return Math.max(1, String(n - 1).length)
  }, [form.total_numbers])

  useEffect(() => {
    if (!token || !canManage) return

    let mounted = true

    const loadPublicTemplateOptions = async () => {
      try {
        const options = await fetchPublicCampaignTemplates(token)
        if (!mounted || options.length === 0) return
        setPublicTemplateOptions(options)
      } catch {
        if (!mounted) return
        setPublicTemplateOptions(FALLBACK_PUBLIC_TEMPLATE_OPTIONS)
      }
    }

    void loadPublicTemplateOptions()

    return () => {
      mounted = false
    }
  }, [canManage, token])

  useEffect(() => {
    let mounted = true

    const load = async () => {
      if (campaignId === null) return

      setIsLoading(true)
      try {
        const campaign = await fetchCampaignById(campaignId, token ?? undefined)
        if (!mounted) return
        const donationOnly = campaign.generation_status === 'donation_only'
        setMarketingFieldErrors({})
        serverHadNumberConfigRef.current =
          donationOnly ||
          (campaign.total_numbers !== null && campaign.total_numbers !== undefined)
        setIsDonationOnlyCampaign(donationOnly)
        const formData = mapCampaignToForm(campaign)
        setLoadedCampaignId(campaign.id)
        savedSellerIdRef.current = formData.doey_seller_id?.trim() ?? ''
        const loadedCarouselItems = mapCampaignCarouselToItems(campaign)
        const loadedTotalNumbersMode = getTotalNumbersMode(
          formData.total_numbers,
          donationOnly
        )
        setForm(formData)
        setExistingMainImageUrl(campaign.main_image_url ?? null)
        setCarouselItems((prev) => {
          revokeCarouselItemPreviews(prev)
          return loadedCarouselItems
        })
        setExistingShareImageUrl(campaign.public_share_image_url ?? null)
        setExistingRegulationPdfUrl(campaign.regulation_pdf_url ?? null)
        setExistingCertificatePdfUrl(campaign.certificate_pdf_url ?? null)
        setSavedSnapshot(
          serializeCampaignEditSnapshot({
            form: formData,
            carouselItems: toCampaignEditCarouselSnapshot(loadedCarouselItems),
            totalNumbersMode: loadedTotalNumbersMode,
            isDonationOnlyCampaign: donationOnly,
          })
        )
        setHasUnsavedChanges(false)
      } catch {
        setSubmitError('Não foi possível carregar a campanha.')
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    load()

    return () => {
      mounted = false
      if (regulationPdfPreview && regulationPdfPreview.startsWith('blob:')) {
        URL.revokeObjectURL(regulationPdfPreview)
      }
      if (certificatePdfPreview && certificatePdfPreview.startsWith('blob:')) {
        URL.revokeObjectURL(certificatePdfPreview)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup revoga blob URLs; incluir previews reexecutaria o effect a cada seleção de arquivo
  }, [campaignId, token, getTotalNumbersMode, setHasUnsavedChanges])

  useEffect(() => {
    return () => {
      revokeCarouselItemPreviews(carouselItemsRef.current)
    }
  }, [])

  const selectedUploadFiles = useMemo(() => {
    const files: Array<{ label: string; file: File }> = []

    if (form.main_image) files.push({ label: 'Imagem principal', file: form.main_image })
    if (form.public_share_image)
      files.push({ label: 'Imagem do compartilhamento', file: form.public_share_image })
    if (form.template3_hero_background)
      files.push({ label: 'Fundo do Template 3', file: form.template3_hero_background })
    if (form.template3_about_image)
      files.push({
        label: 'Imagem Sobre do Template 3',
        file: form.template3_about_image,
      })
    if (form.template3_advantage_image_1)
      files.push({
        label: 'Vantagem 1 do Template 3',
        file: form.template3_advantage_image_1,
      })
    if (form.template3_advantage_image_2)
      files.push({
        label: 'Vantagem 2 do Template 3',
        file: form.template3_advantage_image_2,
      })
    if (form.template3_advantage_image_3)
      files.push({
        label: 'Vantagem 3 do Template 3',
        file: form.template3_advantage_image_3,
      })
    if (form.regulation_pdf)
      files.push({ label: 'Regulamento em PDF', file: form.regulation_pdf })
    if (form.certificate_pdf)
      files.push({ label: 'Certificado em PDF', file: form.certificate_pdf })
    carouselItems.forEach((item, index) => {
      if (item.kind === 'new') {
        files.push({ label: `Imagem do carrossel ${index + 1}`, file: item.file })
      }
    })

    return files
  }, [carouselItems, form])

  const selectedUploadTotalBytes = useMemo(
    () => selectedUploadFiles.reduce((total, item) => total + item.file.size, 0),
    [selectedUploadFiles]
  )

  /** Lógica de salvar: chamada pelo submit do form, pelo botão do header e pelo modal do Template 3. */
  const doSave = useCallback(
    async (formOverride?: CampaignUpsertInput) => {
      const formToSave = formOverride ?? form
      setSubmitError(null)
      setSubmitSuccess(null)

      if (!canManage) {
        setTemplate3MissingModalOpen(false)
        setSubmitError('Apenas instituições podem gerenciar campanhas.')
        return
      }

      if (!token) {
        setTemplate3MissingModalOpen(false)
        setSubmitError('Sessão expirada. Faça login novamente.')
        return
      }

      if (campaignId === null) {
        setTemplate3MissingModalOpen(false)
        setSubmitError('Selecione uma campanha existente para editar.')
        return
      }

      if (!formToSave.name?.trim()) {
        setTemplate3MissingModalOpen(false)
        setActiveTab('identidade')
        setSubmitError('Informe o nome da campanha.')
        return
      }

      if (
        formToSave.regulation_content_type === 'text' &&
        !htmlHasVisibleText(formToSave.regulation_text)
      ) {
        setTemplate3MissingModalOpen(false)
        setActiveTab('certificados-regulamento')
        setSubmitError('Informe o texto do regulamento.')
        return
      }

      const marketingErrors = validateCampaignMarketingFields({
        g_pixel: formToSave.g_pixel,
        gtm_measurement_id: formToSave.gtm_measurement_id?.trim().toUpperCase() ?? '',
        ga4_measurement_protocol_secret: formToSave.ga4_measurement_protocol_secret,
      })
      if (hasCampaignMarketingFieldErrors(marketingErrors)) {
        setMarketingFieldErrors(marketingErrors)
        setActiveTab('pixels')
        setTemplate3MissingModalOpen(false)
        setSubmitError('Corrija os campos de pixels e rastreamento antes de salvar.')
        return
      }
      setMarketingFieldErrors({})

      if (
        matchesPublicCampaignTemplate(
          formToSave.public_template_key,
          TEMPLATE_3_PUBLIC_CAMPAIGN_TEMPLATE_KEY
        )
      ) {
        const missingTemplate3Fields = listMissingTemplate3Fields(formToSave)
        if (missingTemplate3Fields.length > 0) {
          setTemplate3MissingModalOpen(true)
          return
        }
      }

      const rawCustomDomain = formToSave.custom_domain?.trim() ?? ''
      let customDomainToSave = ''
      if (rawCustomDomain !== '') {
        const normalizedCustomDomain = normalizeCustomDomainInput(rawCustomDomain)
        if (
          normalizedCustomDomain === null ||
          isReservedCustomDomainHost(normalizedCustomDomain, env.publicAppUrl)
        ) {
          setActiveTab('dominio-personalizado')
          setTemplate3MissingModalOpen(false)
          setSubmitError(
            'Informe um subdomínio válido para o domínio personalizado, sem http, porta ou caminho.'
          )
          return
        }
        customDomainToSave = normalizedCustomDomain
      }

      if (
        (formToSave.doey_seller_id?.trim() ?? '') !== savedSellerIdRef.current &&
        !formToSave.seller_id_totp_code
      ) {
        pendingSellerChangeRef.current = formToSave
        setSellerIdChangeModalOpen(true)
        return
      }

      setIsSubmitting(true)

      try {
        const carouselOrder = carouselItems.map((item) =>
          item.kind === 'existing' ? `existing:${item.id}` : `new:${item.key}`
        ) as CampaignCarouselOrderToken[]
        const carouselImages = carouselItems.reduce<Record<string, File>>((acc, item) => {
          if (item.kind === 'new') {
            acc[item.key] = item.file
          }

          return acc
        }, {})

        const basePayload: CampaignUpsertInput = {
          ...formToSave,
          starts_at: formToSave.starts_at ? toApiDateTime(formToSave.starts_at) : '',
          ends_at: formToSave.ends_at ? toApiDateTime(formToSave.ends_at) : '',
          public_page_url: normalizeHttpsUrl(formToSave.public_page_url),
          custom_domain: customDomainToSave,
          g_pixel: formToSave.g_pixel?.trim() ?? '',
          gtm_measurement_id: formToSave.gtm_measurement_id?.trim().toUpperCase() ?? '',
          sync_carousel: true,
          carousel_order: carouselOrder,
          carousel_images: carouselImages,
        }

        if (campaignId === null) {
          setSubmitError('Selecione uma campanha existente para editar.')
          return
        } else {
          // Backend só permite definir total_numbers uma vez; se a campanha já tinha, não reenviar
          let updatePayload: CampaignUpsertInput = { ...basePayload }

          if (serverHadNumberConfigRef.current) {
            const rest: CampaignUpsertInput = { ...updatePayload }
            delete rest.total_numbers
            delete rest.donation_only
            updatePayload = rest
          } else if (totalNumbersMode === 'donation') {
            updatePayload = {
              ...updatePayload,
              donation_only: true,
              total_numbers: undefined,
            }
            delete updatePayload.total_numbers
          }

          const updated = await runWithStepUp(() =>
            updateCampaign(token, campaignId, updatePayload)
          )
          const updatedDonationOnly = updated.generation_status === 'donation_only'
          setMarketingFieldErrors({})
          const savedForm = mapCampaignToForm(updated)
          savedSellerIdRef.current = savedForm.doey_seller_id?.trim() ?? ''
          const savedCarouselItems = mapCampaignCarouselToItems(updated)
          const savedTotalNumbersMode = getTotalNumbersMode(
            savedForm.total_numbers,
            updatedDonationOnly
          )
          serverHadNumberConfigRef.current =
            updatedDonationOnly ||
            (updated.total_numbers !== null && updated.total_numbers !== undefined)
          setIsDonationOnlyCampaign(updatedDonationOnly)
          setForm(savedForm)
          setExistingMainImageUrl(updated.main_image_url ?? null)
          setCarouselItems((prev) => {
            revokeCarouselItemPreviews(prev)
            return savedCarouselItems
          })
          setExistingShareImageUrl(updated.public_share_image_url ?? null)
          setExistingRegulationPdfUrl(updated.regulation_pdf_url ?? null)
          setExistingCertificatePdfUrl(updated.certificate_pdf_url ?? null)
          setRegulationPdfPreview(null)
          setCertificatePdfPreview(null)
          setSavedSnapshot(
            serializeCampaignEditSnapshot({
              form: savedForm,
              carouselItems: toCampaignEditCarouselSnapshot(savedCarouselItems),
              totalNumbersMode: savedTotalNumbersMode,
              isDonationOnlyCampaign: updatedDonationOnly,
            })
          )
          setHasUnsavedChanges(false)
          setSubmitSuccess('Campanha atualizada com sucesso.')
          setTemplate3MissingModalOpen(false)
          return true
        }
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 413) {
            const uploadSummary =
              selectedUploadFiles.length > 0
                ? ` Arquivos selecionados nesta edição: ${selectedUploadFiles.length} item(ns), total de ${formatFileSize(selectedUploadTotalBytes)}.`
                : ''
            setSubmitError(
              `O envio ficou grande demais para o servidor processar.${uploadSummary} Reduza o tamanho ou a quantidade de imagens e PDFs e tente novamente.`
            )
          } else {
            const apiMarketingErrors = formatCampaignMarketingApiErrors(error.errors)
            if (hasCampaignMarketingFieldErrors(apiMarketingErrors)) {
              setMarketingFieldErrors(apiMarketingErrors)
              setActiveTab('pixels')
            }
            const firstField = Object.keys(error.errors ?? {})[0]
            const errorTab = firstField ? campaignFieldTab(firstField) : null
            if (errorTab) setActiveTab(errorTab)
            setSubmitError(extractApiErrorMessage(error))
            if (
              error.errors &&
              Object.keys(error.errors).some((key) => key.startsWith('template3_'))
            ) {
              setTemplate3MissingModalOpen(true)
            }
          }
        } else {
          setSubmitError('Não foi possível salvar a campanha.')
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      campaignId,
      setActiveTab,
      carouselItems,
      form,
      canManage,
      token,
      selectedUploadFiles,
      selectedUploadTotalBytes,
      totalNumbersMode,
      getTotalNumbersMode,
      setHasUnsavedChanges,
      runWithStepUp,
    ]
  )

  const handleTemplate3MissingSave = useCallback(
    (draft: CampaignUpsertInput) => {
      setForm(draft)
      void doSave(draft).then((saved) => {
        if (saved) reportSaveSuccess()
      })
    },
    [doSave, reportSaveSuccess]
  )

  const handleTemplate3MissingClose = useCallback(() => {
    if (isSubmitting) {
      return
    }
    setTemplate3MissingModalOpen(false)
  }, [isSubmitting])

  const removeCarouselItem = useCallback((index: number) => {
    setCarouselItems((prev) => {
      const itemToRemove = prev[index]
      if (!itemToRemove) return prev

      const next = prev.filter((_, currentIndex) => currentIndex !== index)
      revokeCarouselItemPreview(itemToRemove)
      return next
    })
  }, [])

  const moveCarouselItem = useCallback((index: number, direction: -1 | 1) => {
    setCarouselItems((prev) => {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= prev.length) return prev

      const next = [...prev]
      const [item] = next.splice(index, 1)
      next.splice(nextIndex, 0, item)
      return next
    })
  }, [])

  const handleCarouselFilesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files ? Array.from(event.target.files) : []

      if (selectedFiles.length === 0) {
        event.target.value = ''
        return
      }

      const totalCount = carouselItems.length + selectedFiles.length
      if (totalCount > MAX_CAROUSEL_IMAGES) {
        setSubmitError(
          `Você pode manter no máximo ${MAX_CAROUSEL_IMAGES} imagens no carrossel.`
        )
        event.target.value = ''
        return
      }

      const tooLargeFile = selectedFiles.find((file) => file.size > MAX_IMAGE_BYTES)
      if (tooLargeFile) {
        setSubmitError(
          `A imagem "${tooLargeFile.name}" é muito grande (${formatFileSize(tooLargeFile.size)}). O limite por arquivo é ${formatFileSize(MAX_IMAGE_BYTES)}.`
        )
        event.target.value = ''
        return
      }

      setSubmitError(null)
      setCarouselItems((prev) => [
        ...prev,
        ...selectedFiles.map((file) => createNewCarouselItem(file)),
      ])
      event.target.value = ''
    },
    [carouselItems.length]
  )

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void doSave().then((saved) => {
        if (saved) reportSaveSuccess()
      })
    },
    [doSave, reportSaveSuccess]
  )

  const campaignHasChanges =
    !isLoading &&
    savedSnapshot !== null &&
    hasCampaignEditChanges(
      {
        form,
        carouselItems: toCampaignEditCarouselSnapshot(carouselItems),
        totalNumbersMode,
        isDonationOnlyCampaign,
      },
      savedSnapshot,
      selectedUploadFiles.length > 0
    )

  // Inclui os rascunhos dos painéis já visitados nesta sessão.
  useEffect(() => {
    if (!canManage || campaignId === null) return
    setSaveTrigger(() => {
      if (savingAllPanelsRef.current) return
      savingAllPanelsRef.current = true
      setIsSavingAllPanels(true)
      void (async () => {
        try {
          const hasGamificationChanges = Boolean(gamificationSave?.hasChanges)
          const hasAvatarChanges = Boolean(avatarSave?.hasChanges)
          const hasAiChanges = Boolean(aiSave?.hasChanges)
          if (hasAiChanges && aiSave && !(await aiSave.save())) {
            setActiveTab('inteligencia-artificial')
            return
          }
          const hasPurchaseScratchChanges = Boolean(purchaseScratchSave?.hasChanges)
          const hasRouletteChanges = Boolean(rouletteSave?.hasChanges)
          const hasApiRouletteChanges = Boolean(apiRouletteSave?.hasChanges)
          if (campaignHasChanges && !(await doSave())) return
          if (hasRouletteChanges && rouletteSave && !(await rouletteSave.save())) {
            setActiveTab('roleta')
            return
          }
          if (hasGamificationChanges && gamificationSave) {
            if (!(await gamificationSave.save())) {
              setActiveTab('gamificacao')
              return
            }
          }
          if (
            hasApiRouletteChanges &&
            apiRouletteSave &&
            !(await apiRouletteSave.save())
          ) {
            setActiveTab('roleta-api')
            return
          }
          if (hasAvatarChanges && avatarSave && !(await avatarSave.save())) {
            setActiveTab('avatar')
            return
          }
          if (
            hasPurchaseScratchChanges &&
            purchaseScratchSave &&
            !(await purchaseScratchSave.save())
          ) {
            setActiveTab('raspadinha-compra')
            return
          }
          if (
            campaignHasChanges ||
            hasGamificationChanges ||
            hasAvatarChanges ||
            hasAiChanges ||
            hasPurchaseScratchChanges ||
            hasRouletteChanges ||
            hasApiRouletteChanges
          )
            reportSaveSuccess()
        } finally {
          savingAllPanelsRef.current = false
          setIsSavingAllPanels(false)
        }
      })()
    })
    return () => setSaveTrigger(null)
  }, [
    canManage,
    campaignId,
    campaignHasChanges,
    gamificationSave,
    avatarSave,
    aiSave,
    purchaseScratchSave,
    rouletteSave,
    apiRouletteSave,
    setSaveTrigger,
    doSave,
    setActiveTab,
    reportSaveSuccess,
  ])

  useEffect(() => {
    setViewPageUrl(
      resolveCampaignViewPageUrl({
        slug: form.slug,
        publicPageUrl: form.public_page_url,
        customDomain: form.custom_domain,
      })
    )
  }, [form.custom_domain, form.public_page_url, form.slug, setViewPageUrl])

  useEffect(() => {
    return () => setViewPageUrl(null)
  }, [setViewPageUrl])

  useEffect(() => {
    setCampaignStatusControl(
      isLoading || savedSnapshot === null
        ? null
        : {
            value: form.status ?? 'draft',
            onChange: (status) => setForm((prev) => ({ ...prev, status })),
          }
    )
    return () => setCampaignStatusControl(null)
  }, [form.status, isLoading, savedSnapshot, setCampaignStatusControl])

  useEffect(() => {
    setLayoutSubmitting(
      isSubmitting ||
        isSavingAllPanels ||
        Boolean(gamificationSave?.saving) ||
        Boolean(avatarSave?.saving) ||
        Boolean(aiSave?.saving) ||
        Boolean(purchaseScratchSave?.saving) ||
        Boolean(rouletteSave?.saving) ||
        Boolean(apiRouletteSave?.saving)
    )
  }, [
    isSubmitting,
    isSavingAllPanels,
    gamificationSave?.saving,
    avatarSave?.saving,
    aiSave?.saving,
    purchaseScratchSave?.saving,
    rouletteSave?.saving,
    apiRouletteSave?.saving,
    setLayoutSubmitting,
  ])

  useEffect(() => {
    setHasUnsavedChanges(
      campaignHasChanges ||
        Boolean(gamificationSave?.hasChanges) ||
        Boolean(avatarSave?.hasChanges) ||
        Boolean(aiSave?.hasChanges) ||
        Boolean(purchaseScratchSave?.hasChanges) ||
        Boolean(rouletteSave?.hasChanges) ||
        Boolean(apiRouletteSave?.hasChanges)
    )
  }, [
    campaignHasChanges,
    gamificationSave?.hasChanges,
    avatarSave?.hasChanges,
    aiSave?.hasChanges,
    purchaseScratchSave?.hasChanges,
    rouletteSave?.hasChanges,
    apiRouletteSave?.hasChanges,
    setHasUnsavedChanges,
  ])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (campaignId === null) {
      setSavedSnapshot(null)
      setHasUnsavedChanges(false)
    }
  }, [campaignId, setHasUnsavedChanges])

  useEffect(() => {
    return () => {
      setHasUnsavedChanges(false)
    }
  }, [setHasUnsavedChanges])

  useEffect(() => {
    if (!submitError && !submitSuccess) return

    alertsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [submitError, submitSuccess])

  const isTemplate3Selected = matchesPublicCampaignTemplate(
    form.public_template_key,
    TEMPLATE_3_PUBLIC_CAMPAIGN_TEMPLATE_KEY
  )

  useEffect(() => {
    if (isLoading || savedSnapshot === null) return
    if (activeTab === 'conteudo-template' && !isTemplate3Selected) {
      setActiveTab('template', true)
    } else if (activeTab === 'numeros-premiados' && isDonationOnlyCampaign) {
      setActiveTab('combos', true)
    }
  }, [
    activeTab,
    isLoading,
    savedSnapshot,
    isTemplate3Selected,
    isDonationOnlyCampaign,
    setActiveTab,
  ])

  if (!canManage) {
    return (
      <div className={styles.editPage}>
        <div className={styles.editForbidden}>
          <div className={styles.editForbiddenMessage} role="alert">
            Apenas instituições podem editar campanhas.
          </div>
        </div>
      </div>
    )
  }

  if (campaignId === null) {
    return <Navigate to="/campanhas" replace />
  }

  return (
    <div className={`${styles.editPage} ${fixedSaveStyles.editPageWithFixedSave}`}>
      {isLoading ? (
        <div className={styles.editLoadingState}>Carregando campanha...</div>
      ) : (
        <ContainerTabs
          value={activeTab}
          onChange={setActiveTab}
          className={styles.containerTabs}
        >
          <CampaignEditorSectionHeader
            tab={activeTab}
            templateName={
              getPublicCampaignTemplateDefinition(form.public_template_key).name
            }
          />
          <ContainerTabsList
            className={editorStyles.subTabs}
            aria-label={activeGroup.title}
          >
            {CAMPAIGN_EDITOR_TABS.filter(
              (tab) =>
                tab.group === activeGroup.id &&
                (tab.id !== 'conteudo-template' || isTemplate3Selected) &&
                (tab.id !== 'numeros-premiados' || !isDonationOnlyCampaign)
            ).map((tab) => (
              <ContainerTab key={tab.id} value={tab.id} icon={tab.icon}>
                {tab.label}
              </ContainerTab>
            ))}
          </ContainerTabsList>

          <form
            id="campanha-edit-form"
            className={styles.editForm}
            onSubmit={handleSubmit}
          >
            {(isSubmitting ||
              selectedUploadFiles.length > 0 ||
              submitError ||
              submitSuccess) && (
              <div ref={alertsRef} className={styles.editAlerts}>
                {isSubmitting && (
                  <div className={styles.editInfoBanner} role="status" aria-live="polite">
                    Salvando campanha. Aguarde enquanto os dados e arquivos são enviados.
                  </div>
                )}
                {!isSubmitting && selectedUploadFiles.length > 0 && (
                  <div className={styles.editInfoBanner} role="status">
                    Arquivos selecionados para envio nesta edição:{' '}
                    {selectedUploadFiles.length} item(ns), total de{' '}
                    {formatFileSize(selectedUploadTotalBytes)}.
                  </div>
                )}
                {submitError && (
                  <div className={styles.editErrorBanner} role="alert">
                    {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className={styles.editSuccessBanner} role="status">
                    {submitSuccess}
                  </div>
                )}
              </div>
            )}

            <ContainerTabPanel value="identidade">
              <section
                className={styles.editSection}
                aria-labelledby="section-identidade"
              >
                <h2 id="section-identidade" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:tag-outline"
                    aria-hidden
                  />
                  Dados gerais
                </h2>
                <div className={styles.editSectionBody}>
                  <div className={styles.editGrid2}>
                    <div className={styles.editField}>
                      <label className={styles.editLabel} htmlFor="campaign-name">
                        <span>
                          Nome da campanha *{' '}
                          <span className={styles.requiredBadge}>Obrigatório</span>
                        </span>
                        <span className={styles.editLabelHint}>
                          Use um nome fácil de identificar na lista de campanhas.
                        </span>
                      </label>
                      <input
                        id="campaign-name"
                        type="text"
                        className={styles.editInput}
                        placeholder="Ex.: Campanha de Natal"
                        maxLength={255}
                        value={form.name ?? ''}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.editSection}>
                <div className={styles.editSectionBody}>
                  <div className={styles.editGridWide}>
                    <div className={styles.editField}>
                      <label className={styles.editLabel} htmlFor="campaign-prize">
                        Descrição do prêmio
                        <span className={styles.editLabelHint}>
                          (Detalhes completos do prêmio que será sorteado)
                        </span>
                      </label>
                      <CampaignRichTextField
                        id="campaign-prize"
                        variant="block"
                        placeholder="Descreva o prêmio em detalhes: modelo, especificações, valor aproximado, condições, etc..."
                        aria-label="Descrição do prêmio"
                        value={form.prize_description ?? ''}
                        onChange={(html) =>
                          setForm((prev) => ({ ...prev, prize_description: html }))
                        }
                        minHeight={96}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <CampaignPublicPageFields
              form={form}
              setForm={setForm}
              setSubmitError={setSubmitError}
              publicTemplateOptions={publicTemplateOptions}
              existingShareImageUrl={existingShareImageUrl}
              imageAccept={CAMPAIGN_IMAGE_ACCEPT}
              maxImageBytes={MAX_IMAGE_BYTES}
              validateFileSize={validateFileSize}
              normalizeHttpsUrl={normalizeHttpsUrl}
              onNavigate={setActiveTab}
            />

            {campaignId !== null && (
              <ContainerTabPanel value="dashboard">
                <section
                  className={styles.editSection}
                  aria-labelledby="section-dashboard"
                >
                  <h2 id="section-dashboard" className={styles.editSectionTitle}>
                    <Icon
                      className={styles.editSectionTitleIcon}
                      icon="mdi:chart-donut"
                      aria-hidden
                    />
                    Dashboard da campanha
                  </h2>
                  <div className={styles.editSectionBody}>
                    <CampaignDashboardContainer campaignId={campaignId} />
                  </div>
                </section>
              </ContainerTabPanel>
            )}

            <ContainerTabPanel value="periodo-valores">
              <section className={styles.editSection} aria-labelledby="section-periodo">
                <h2 id="section-periodo" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:calendar-clock-outline"
                    aria-hidden
                  />
                  Valores e período
                </h2>
                <div className={styles.editSectionBody}>
                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>
                      Quando a campanha acontece
                    </h3>
                    <div className={styles.editGrid2}>
                      <div className={styles.editField}>
                        <label className={styles.editLabel} htmlFor="campaign-starts">
                          Data e hora de início
                          <span className={styles.editLabelHint}>
                            (Quando a campanha começa a aceitar participações)
                          </span>
                        </label>
                        <input
                          id="campaign-starts"
                          type="datetime-local"
                          className={styles.editInput}
                          value={form.starts_at ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, starts_at: e.target.value }))
                          }
                        />
                      </div>
                      <div className={styles.editField}>
                        <label className={styles.editLabel} htmlFor="campaign-ends">
                          Data e hora de encerramento
                          <span className={styles.editLabelHint}>
                            (Quando a campanha para de aceitar participações)
                          </span>
                        </label>
                        <input
                          id="campaign-ends"
                          type="datetime-local"
                          className={styles.editInput}
                          value={form.ends_at ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, ends_at: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>
                      Preço e limites de participação
                    </h3>
                    <div className={`${styles.editGrid3} ${styles.alignedFieldsGrid}`}>
                      <div className={styles.alignedField}>
                        <label className={styles.editLabel} htmlFor="campaign-price">
                          Preço por número
                        </label>
                        <CurrencyInput
                          id="campaign-price"
                          aria-describedby="campaign-price-help"
                          value={form.price}
                          onChange={(value) =>
                            setForm((prev) => ({ ...prev, price: value }))
                          }
                          inputAsCents={true}
                          placeholder="Digite o preço (ex: 380 = R$ 3,80)"
                        />
                        <p id="campaign-price-help" className={styles.fieldHelp}>
                          Valor pago pelo participante por cada número.
                        </p>
                      </div>
                      <div className={styles.alignedField}>
                        <label className={styles.editLabel} htmlFor="campaign-min-qty">
                          Quantidade mínima por compra
                        </label>
                        <input
                          id="campaign-min-qty"
                          aria-describedby="campaign-min-qty-help"
                          type="number"
                          className={styles.editInput}
                          placeholder="Ex: 1"
                          min="1"
                          value={form.min_quantity_per_purchase ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              min_quantity_per_purchase: e.target.value,
                            }))
                          }
                        />
                        <p id="campaign-min-qty-help" className={styles.fieldHelp}>
                          Menor quantidade de números permitida em uma compra.
                        </p>
                      </div>
                      <div className={styles.alignedField}>
                        <label className={styles.editLabel} htmlFor="campaign-max-qty">
                          Quantidade máxima por compra
                        </label>
                        <input
                          id="campaign-max-qty"
                          aria-describedby="campaign-max-qty-help"
                          type="number"
                          className={styles.editInput}
                          placeholder="Ex: 100"
                          min="1"
                          value={form.max_quantity_per_purchase ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              max_quantity_per_purchase: e.target.value,
                            }))
                          }
                        />
                        <p id="campaign-max-qty-help" className={styles.fieldHelp}>
                          Maior quantidade de números permitida em uma compra.
                        </p>
                      </div>
                    </div>
                    <div className={styles.fieldGroupDivider}>
                      <div className={`${styles.editField} ${styles.compactField}`}>
                        <label
                          className={styles.editLabel}
                          htmlFor="campaign-pix-expiration"
                        >
                          Prazo para pagamento do Pix
                        </label>
                        <select
                          id="campaign-pix-expiration"
                          aria-describedby="campaign-pix-expiration-help"
                          className={styles.editSelect}
                          value={form.pix_qr_expiration_minutes ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              pix_qr_expiration_minutes: e.target.value,
                            }))
                          }
                        >
                          {PIX_QR_EXPIRATION_OPTIONS.map((option) => (
                            <option
                              key={option.value || 'unlimited'}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p id="campaign-pix-expiration-help" className={styles.fieldHelp}>
                          Tempo disponível para pagar cada novo QR Code Pix gerado na
                          página da campanha ou no botão de compra de outro site.
                        </p>
                      </div>
                      <div className={`${styles.editField} ${styles.fieldGroupDivider}`}>
                        <label
                          className={styles.editLabel}
                          htmlFor="campaign-total-numbers"
                        >
                          Total de números disponíveis
                          <span className={styles.editLabelHint}>
                            {isDonationOnlyCampaign
                              ? '(Campanha configurada para compras sem geração de números da sorte)'
                              : '(Quantidade total de números que serão gerados para esta campanha - Máx: 100.000.000)'}
                          </span>
                        </label>
                        {serverHadNumberConfigRef.current && isDonationOnlyCampaign && (
                          <p
                            className={styles.editLabelHint}
                            style={{ marginBottom: '0.75rem' }}
                          >
                            Esta campanha aceita compras via PIX. Os participantes recebem
                            confirmação de pagamento, sem números da sorte.
                          </p>
                        )}
                        <div className={styles.totalNumbersContainer}>
                          <div className={styles.importantNote}>
                            <Icon
                              icon={
                                serverHadNumberConfigRef.current
                                  ? 'mdi:lock-outline'
                                  : 'mdi:information-outline'
                              }
                              aria-hidden
                            />
                            <p>
                              <strong>
                                {serverHadNumberConfigRef.current
                                  ? 'Configuração já definida.'
                                  : 'Esta escolha é definitiva.'}
                              </strong>{' '}
                              {serverHadNumberConfigRef.current
                                ? 'A quantidade de números e o modo de participação não podem mais ser alterados.'
                                : 'Depois de salvar a quantidade total de números ou a opção Compra sem números da sorte, você não poderá mudar essa configuração.'}
                            </p>
                          </div>
                          <div className={styles.totalNumbersPresets}>
                            {TOTAL_NUMBERS_PRESETS.map((preset) => {
                              const isSelected =
                                form.total_numbers === preset.value &&
                                totalNumbersMode === 'preset'
                              return (
                                <button
                                  key={preset.value}
                                  type="button"
                                  disabled={serverHadNumberConfigRef.current}
                                  className={`${styles.totalNumbersPreset} ${isSelected ? styles.totalNumbersPresetActive : ''}`}
                                  onClick={() => {
                                    setTotalNumbersMode('preset')
                                    setIsDonationOnlyCampaign(false)
                                    setForm((prev) => ({
                                      ...prev,
                                      total_numbers: preset.value,
                                    }))
                                  }}
                                >
                                  {preset.label}
                                </button>
                              )
                            })}
                            <button
                              type="button"
                              disabled={serverHadNumberConfigRef.current}
                              className={`${styles.totalNumbersPreset} ${totalNumbersMode === 'custom' ? styles.totalNumbersPresetActive : ''}`}
                              onClick={() => {
                                setTotalNumbersMode('custom')
                                setIsDonationOnlyCampaign(false)
                                if (
                                  !form.total_numbers ||
                                  TOTAL_NUMBERS_PRESETS.some(
                                    (p) => p.value === form.total_numbers
                                  )
                                ) {
                                  setForm((prev) => ({ ...prev, total_numbers: '' }))
                                }
                              }}
                            >
                              Personalizado
                            </button>
                            <button
                              type="button"
                              disabled={serverHadNumberConfigRef.current}
                              className={`${styles.totalNumbersPreset} ${totalNumbersMode === 'donation' ? styles.totalNumbersPresetActive : ''}`}
                              onClick={() => {
                                setTotalNumbersMode('donation')
                                setIsDonationOnlyCampaign(true)
                                setForm((prev) => ({ ...prev, total_numbers: '' }))
                              }}
                            >
                              Compra sem números da sorte
                            </button>
                          </div>
                          {totalNumbersMode === 'custom' && !isDonationOnlyCampaign && (
                            <div className={styles.totalNumbersCustom}>
                              <input
                                id="campaign-total-numbers"
                                type="number"
                                className={styles.editInput}
                                placeholder="Digite o valor (máx: 100.000.000)"
                                min="1"
                                max={MAX_TOTAL_NUMBERS}
                                value={form.total_numbers ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value
                                  if (!value) {
                                    setForm((prev) => ({ ...prev, total_numbers: '' }))
                                    return
                                  }
                                  const numValue = parseInt(value, 10)
                                  if (!Number.isNaN(numValue) && numValue > 0) {
                                    if (numValue <= MAX_TOTAL_NUMBERS) {
                                      setForm((prev) => ({
                                        ...prev,
                                        total_numbers: value,
                                      }))
                                    } else {
                                      // Limita ao máximo permitido
                                      setForm((prev) => ({
                                        ...prev,
                                        total_numbers: String(MAX_TOTAL_NUMBERS),
                                      }))
                                    }
                                  }
                                }}
                              />
                              {form.total_numbers &&
                                parseInt(form.total_numbers, 10) > MAX_TOTAL_NUMBERS && (
                                  <span className={styles.totalNumbersError}>
                                    Valor máximo:{' '}
                                    {MAX_TOTAL_NUMBERS.toLocaleString('pt-BR')}
                                  </span>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>Recebimento pela Doey</h3>
                    <p className={styles.editSubsectionLead}>
                      Preencha o identificador fornecido pela Doey quando essa integração
                      for usada na campanha.
                    </p>
                    <div className={styles.editGrid2}>
                      <div className={styles.editField} style={{ gridColumn: '1 / -1' }}>
                        <label
                          className={styles.editLabel}
                          htmlFor="campaign-doey-seller-id"
                        >
                          ID do vendedor (Doey)
                          <span className={styles.editLabelHint}>
                            (Identificador do vendedor na plataforma Doey para
                            processamento de pagamentos)
                          </span>
                        </label>
                        <input
                          id="campaign-doey-seller-id"
                          type="text"
                          className={styles.editInput}
                          maxLength={36}
                          placeholder="Ex: 123e4567-e89b-12d3-a456-426614174000"
                          value={form.doey_seller_id ?? ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              doey_seller_id: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="comunicacao-midia">
              <section
                className={styles.editSection}
                aria-labelledby="section-comunicacao"
              >
                <h2 id="section-comunicacao" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:palette-outline"
                    aria-hidden
                  />
                  Imagens e cores
                </h2>
                <div className={styles.editSectionBody}>
                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>
                      Imagem principal e galeria
                    </h3>
                    <p className={styles.editSubsectionLead}>
                      Escolha fotos nítidas que apresentem o prêmio ou a causa. Você pode
                      ampliar a imagem e desfazer a troca antes de salvar.
                    </p>
                    <div className={styles.editMediaGrid}>
                      <CampaignMainImageField
                        file={form.main_image ?? null}
                        imageUrl={existingMainImageUrl}
                        hint={
                          isTemplate3Selected
                            ? TEMPLATE_3_MAIN_IMAGE_HINT
                            : DEFAULT_MAIN_IMAGE_HINT
                        }
                        onFileChange={(nextFile) =>
                          setForm((prev) => ({ ...prev, main_image: nextFile }))
                        }
                        onFileError={setSubmitError}
                      />
                      {!isTemplate3Selected && (
                        <div className={styles.editMediaBlock}>
                          <label className={styles.editLabel}>
                            Galeria da campanha
                            <span className={styles.editLabelHint}>
                              (Até {MAX_CAROUSEL_IMAGES} imagens no total, com ordenação
                              manual)
                            </span>
                          </label>
                          <input
                            ref={carouselImagesInputRef}
                            id="campaign-carousel-images"
                            type="file"
                            accept={CAMPAIGN_IMAGE_ACCEPT}
                            multiple
                            className={styles.fileInput}
                            onChange={handleCarouselFilesChange}
                          />
                          <div
                            className={`${styles.editUploadZone} ${carouselItems.length > 0 ? styles.editUploadZoneHasFile : ''}`}
                          >
                            {carouselItems.length > 0 ? (
                              <>
                                <div className={styles.carouselSummary}>
                                  <span className={styles.carouselCounter}>
                                    {carouselItems.length} de {MAX_CAROUSEL_IMAGES}{' '}
                                    imagens no carrossel
                                  </span>
                                  <span className={styles.editUploadHint}>
                                    A imagem principal continua aparecendo primeiro na
                                    página pública. As imagens abaixo aparecem em seguida,
                                    na ordem salva.
                                  </span>
                                </div>
                                <div className={styles.editPreviewCarousel}>
                                  {carouselItems.map((item, index) => (
                                    <div
                                      className={styles.editPreviewCarouselItem}
                                      key={
                                        item.kind === 'existing'
                                          ? `existing-${item.id}`
                                          : `new-${item.key}`
                                      }
                                    >
                                      <img
                                        src={
                                          item.kind === 'existing'
                                            ? item.imageUrl
                                            : item.previewUrl
                                        }
                                        alt={`Imagem ${index + 1} do carrossel`}
                                      />
                                      <span className={styles.carouselOrderBadge}>
                                        {index + 1}
                                      </span>
                                      <span className={styles.carouselItemStatus}>
                                        {item.kind === 'new' ? 'Nova' : 'Salva'}
                                      </span>
                                      <button
                                        type="button"
                                        className={styles.removePreviewBtn}
                                        onClick={() => removeCarouselItem(index)}
                                        title={
                                          item.kind === 'existing'
                                            ? 'Remover imagem do carrossel'
                                            : 'Remover imagem selecionada'
                                        }
                                      >
                                        <Icon icon="mdi:close" />
                                      </button>
                                      <div className={styles.carouselMoveActions}>
                                        <button
                                          type="button"
                                          className={styles.carouselMoveBtn}
                                          onClick={() => moveCarouselItem(index, -1)}
                                          disabled={index === 0}
                                          title="Mover imagem para a esquerda"
                                        >
                                          <Icon icon="mdi:chevron-left" />
                                        </button>
                                        <button
                                          type="button"
                                          className={styles.carouselMoveBtn}
                                          onClick={() => moveCarouselItem(index, 1)}
                                          disabled={index === carouselItems.length - 1}
                                          title="Mover imagem para a direita"
                                        >
                                          <Icon icon="mdi:chevron-right" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                  {carouselItems.length < MAX_CAROUSEL_IMAGES && (
                                    <button
                                      type="button"
                                      className={styles.addMoreCarouselBtn}
                                      onClick={() =>
                                        carouselImagesInputRef.current?.click()
                                      }
                                    >
                                      <Icon icon="mdi:plus" />
                                      <span>Adicionar</span>
                                    </button>
                                  )}
                                </div>
                              </>
                            ) : (
                              <button
                                type="button"
                                className={styles.editUploadTrigger}
                                onClick={() => carouselImagesInputRef.current?.click()}
                              >
                                <Icon
                                  icon="mdi:image-multiple-outline"
                                  style={{
                                    fontSize: 28,
                                    color: 'var(--color-text-muted)',
                                  }}
                                  aria-hidden
                                />
                                <span className={styles.editUploadHint}>
                                  Adicione até {MAX_CAROUSEL_IMAGES} imagens PNG, JPG,
                                  WEBP ou GIF, com até 5 MB cada
                                </span>
                                <span className={styles.editUploadButton}>
                                  Selecionar imagens
                                </span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>Paleta de cores</h3>
                    <div className={styles.editColorGrid}>
                      <div className={styles.editColorCard}>
                        <div className={styles.editColorCardHeader}>
                          <label
                            htmlFor="campaign-primary"
                            className={styles.editColorLabel}
                          >
                            Cor primária
                          </label>
                          <span className={styles.editLabelHint}>
                            (Cor principal da campanha - títulos, destaques)
                          </span>
                        </div>
                        <div className={styles.editColorPickerWrapper}>
                          <div
                            className={styles.editColorPreview}
                            style={{ backgroundColor: themeColors.primaryColor }}
                          >
                            <input
                              id="campaign-primary"
                              type="color"
                              className={styles.editColorInput}
                              value={themeColors.primaryColor}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  primary_color: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className={styles.editColorValue}>
                            <span className={styles.editColorHex}>
                              {themeColors.primaryColor}
                            </span>
                            <button
                              type="button"
                              className={styles.editColorCopyBtn}
                              onClick={() => {
                                navigator.clipboard.writeText(themeColors.primaryColor)
                              }}
                              title="Copiar código da cor"
                            >
                              <Icon icon="mdi:content-copy" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className={styles.editColorCard}>
                        <div className={styles.editColorCardHeader}>
                          <label
                            htmlFor="campaign-secondary"
                            className={styles.editColorLabel}
                          >
                            Cor secundária
                          </label>
                          <span className={styles.editLabelHint}>
                            (Cor complementar - elementos secundários)
                          </span>
                        </div>
                        <div className={styles.editColorPickerWrapper}>
                          <div
                            className={styles.editColorPreview}
                            style={{ backgroundColor: themeColors.secondaryColor }}
                          >
                            <input
                              id="campaign-secondary"
                              type="color"
                              className={styles.editColorInput}
                              value={themeColors.secondaryColor}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  secondary_color: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className={styles.editColorValue}>
                            <span className={styles.editColorHex}>
                              {themeColors.secondaryColor}
                            </span>
                            <button
                              type="button"
                              className={styles.editColorCopyBtn}
                              onClick={() => {
                                navigator.clipboard.writeText(themeColors.secondaryColor)
                              }}
                              title="Copiar código da cor"
                            >
                              <Icon icon="mdi:content-copy" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className={styles.editColorCard}>
                        <div className={styles.editColorCardHeader}>
                          <label
                            htmlFor="campaign-button"
                            className={styles.editColorLabel}
                          >
                            Cor dos botões
                          </label>
                          <span className={styles.editLabelHint}>
                            (Cor dos botões de ação e CTA)
                          </span>
                        </div>
                        <div className={styles.editColorPickerWrapper}>
                          <div
                            className={styles.editColorPreview}
                            style={{ backgroundColor: themeColors.buttonColor }}
                          >
                            <input
                              id="campaign-button"
                              type="color"
                              className={styles.editColorInput}
                              value={themeColors.buttonColor}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  button_color: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className={styles.editColorValue}>
                            <span className={styles.editColorHex}>
                              {themeColors.buttonColor}
                            </span>
                            <button
                              type="button"
                              className={styles.editColorCopyBtn}
                              onClick={() => {
                                navigator.clipboard.writeText(themeColors.buttonColor)
                              }}
                              title="Copiar código da cor"
                            >
                              <Icon icon="mdi:content-copy" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className={styles.editColorCard}>
                        <div className={styles.editColorCardHeader}>
                          <label
                            htmlFor="campaign-highlight"
                            className={styles.editColorLabel}
                          >
                            Cor de destaque
                          </label>
                          <span className={styles.editLabelHint}>
                            (Cor para destacar informações importantes)
                          </span>
                        </div>
                        <div className={styles.editColorPickerWrapper}>
                          <div
                            className={styles.editColorPreview}
                            style={{ backgroundColor: themeColors.highlightColor }}
                          >
                            <input
                              id="campaign-highlight"
                              type="color"
                              className={styles.editColorInput}
                              value={themeColors.highlightColor}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  highlight_color: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className={styles.editColorValue}>
                            <span className={styles.editColorHex}>
                              {themeColors.highlightColor}
                            </span>
                            <button
                              type="button"
                              className={styles.editColorCopyBtn}
                              onClick={() => {
                                navigator.clipboard.writeText(themeColors.highlightColor)
                              }}
                              title="Copiar código da cor"
                            >
                              <Icon icon="mdi:content-copy" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="certificados-regulamento">
              <section
                className={styles.editSection}
                aria-labelledby="section-certificados"
              >
                <h2 id="section-certificados" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:file-document-outline"
                    aria-hidden
                  />
                  Regras e documentos da campanha
                </h2>
                <div className={styles.editSectionBody}>
                  <div className={styles.editSubsection}>
                    <h3 className={styles.editSubsectionTitle}>
                      Como apresentar o regulamento
                    </h3>
                    <p className={styles.editSubsectionLead}>
                      Envie um documento PDF ou escreva as regras abaixo. A opção
                      selecionada será exibida aos participantes.
                    </p>
                    <ContainerTabs
                      value={
                        (form.regulation_content_type ??
                          'document') as RegulationContentType
                      }
                      onChange={(tab) => {
                        setForm((prev) => ({
                          ...prev,
                          regulation_content_type: (tab === 'text'
                            ? 'text'
                            : 'document') as RegulationContentType,
                        }))
                      }}
                      className={styles.editInnerTabs}
                    >
                      <ContainerTabsList aria-label="Modo do regulamento">
                        <ContainerTab value="document" icon="mdi:file-pdf-box">
                          Documento
                        </ContainerTab>
                        <ContainerTab value="text" icon="mdi:text-box-outline">
                          Texto
                        </ContainerTab>
                      </ContainerTabsList>
                    </ContainerTabs>
                    <div className={styles.editMediaGrid}>
                      {form.regulation_content_type === 'document' ? (
                        <div className={styles.editMediaBlock}>
                          <label className={styles.editLabel}>
                            Regulamento da campanha
                            <span className={styles.editLabelHint}>
                              (PDF com as regras e condições do sorteio)
                            </span>
                          </label>
                          <input
                            id="campaign-regulation-pdf"
                            type="file"
                            accept="application/pdf"
                            className={styles.fileInput}
                            onChange={(e) => {
                              const file = e.target.files?.[0] ?? null
                              if (file) {
                                const validationError = validateFileSize(
                                  file,
                                  MAX_PDF_BYTES,
                                  'O regulamento em PDF'
                                )
                                if (validationError) {
                                  setSubmitError(validationError)
                                  e.target.value = ''
                                  return
                                }
                              }
                              setSubmitError(null)
                              setForm((prev) => ({ ...prev, regulation_pdf: file }))
                              if (
                                regulationPdfPreview &&
                                regulationPdfPreview.startsWith('blob:')
                              ) {
                                URL.revokeObjectURL(regulationPdfPreview)
                              }
                              setRegulationPdfPreview(
                                file ? URL.createObjectURL(file) : null
                              )
                            }}
                          />
                          <label
                            htmlFor="campaign-regulation-pdf"
                            className={`${styles.editUploadZone} ${form.regulation_pdf || existingRegulationPdfUrl ? styles.editUploadZoneHasFile : ''}`}
                          >
                            {form.regulation_pdf ? (
                              <div className={styles.editPdfPreview}>
                                <Icon
                                  icon="mdi:file-pdf-box"
                                  style={{ fontSize: 48, color: '#dc2626' }}
                                  aria-hidden
                                />
                                <span className={styles.editPdfFileName}>
                                  {form.regulation_pdf?.name ?? 'Regulamento.pdf'}
                                </span>
                                <span className={styles.editPdfFileSize}>
                                  {form.regulation_pdf.size
                                    ? `${(form.regulation_pdf.size / 1024 / 1024).toFixed(2)} MB`
                                    : ''}
                                </span>
                                <a
                                  href={regulationPdfPreview ?? '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.editPdfViewButton}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon
                                    icon="mdi:open-in-new"
                                    style={{ fontSize: 16 }}
                                    aria-hidden
                                  />
                                  Visualizar PDF
                                </a>
                              </div>
                            ) : existingRegulationPdfUrl ? (
                              <div className={styles.editPdfPreview}>
                                <Icon
                                  icon="mdi:file-pdf-box"
                                  style={{ fontSize: 48, color: '#dc2626' }}
                                  aria-hidden
                                />
                                <span className={styles.editPdfFileName}>
                                  Documento atual enviado
                                </span>
                                <a
                                  href={existingRegulationPdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.editPdfViewButton}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon
                                    icon="mdi:open-in-new"
                                    style={{ fontSize: 16 }}
                                    aria-hidden
                                  />
                                  Visualizar PDF
                                </a>
                              </div>
                            ) : (
                              <>
                                <Icon
                                  icon="mdi:file-pdf-box-outline"
                                  style={{
                                    fontSize: 28,
                                    color: 'var(--color-text-muted)',
                                  }}
                                  aria-hidden
                                />
                                <span className={styles.editUploadHint}>
                                  PDF, até 10MB
                                </span>
                                <span className={styles.editUploadButton}>
                                  Escolher arquivo
                                </span>
                              </>
                            )}
                          </label>
                        </div>
                      ) : (
                        <div className={styles.editField}>
                          <label
                            className={styles.editLabel}
                            htmlFor="campaign-regulation-text"
                          >
                            <span>
                              Texto do regulamento{' '}
                              <span className={styles.requiredBadge}>Obrigatório</span>
                            </span>
                            <span className={styles.editLabelHint}>
                              Obrigatório ao escolher o formato Texto. Estas regras
                              aparecem no botão Regulamento da página.
                            </span>
                          </label>
                          <CampaignRichTextField
                            id="campaign-regulation-text"
                            variant="block"
                            placeholder="Digite aqui o texto completo do regulamento."
                            aria-label="Texto do regulamento"
                            aria-required
                            value={form.regulation_text ?? ''}
                            onChange={(html) =>
                              setForm((prev) => ({ ...prev, regulation_text: html }))
                            }
                            minHeight={220}
                          />
                        </div>
                      )}
                      {form.regulation_content_type === 'document' && (
                        <div className={styles.editMediaBlock}>
                          <label className={styles.editLabel}>
                            Certificado da campanha
                            <span className={styles.editLabelHint}>
                              (PDF com o certificado de autorização do sorteio)
                            </span>
                          </label>
                          <input
                            id="campaign-certificate-pdf"
                            type="file"
                            accept="application/pdf"
                            className={styles.fileInput}
                            onChange={(e) => {
                              const file = e.target.files?.[0] ?? null
                              if (file) {
                                const validationError = validateFileSize(
                                  file,
                                  MAX_PDF_BYTES,
                                  'O certificado em PDF'
                                )
                                if (validationError) {
                                  setSubmitError(validationError)
                                  e.target.value = ''
                                  return
                                }
                              }
                              setSubmitError(null)
                              setForm((prev) => ({ ...prev, certificate_pdf: file }))
                              if (
                                certificatePdfPreview &&
                                certificatePdfPreview.startsWith('blob:')
                              ) {
                                URL.revokeObjectURL(certificatePdfPreview)
                              }
                              setCertificatePdfPreview(
                                file ? URL.createObjectURL(file) : null
                              )
                            }}
                          />
                          <label
                            htmlFor="campaign-certificate-pdf"
                            className={`${styles.editUploadZone} ${form.certificate_pdf || existingCertificatePdfUrl ? styles.editUploadZoneHasFile : ''}`}
                          >
                            {form.certificate_pdf ? (
                              <div className={styles.editPdfPreview}>
                                <Icon
                                  icon="mdi:file-pdf-box"
                                  style={{ fontSize: 48, color: '#dc2626' }}
                                  aria-hidden
                                />
                                <span className={styles.editPdfFileName}>
                                  {form.certificate_pdf?.name ?? 'Certificado.pdf'}
                                </span>
                                <span className={styles.editPdfFileSize}>
                                  {form.certificate_pdf.size
                                    ? `${(form.certificate_pdf.size / 1024 / 1024).toFixed(2)} MB`
                                    : ''}
                                </span>
                                <a
                                  href={certificatePdfPreview ?? '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.editPdfViewButton}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon
                                    icon="mdi:open-in-new"
                                    style={{ fontSize: 16 }}
                                    aria-hidden
                                  />
                                  Visualizar PDF
                                </a>
                              </div>
                            ) : existingCertificatePdfUrl ? (
                              <div className={styles.editPdfPreview}>
                                <Icon
                                  icon="mdi:file-pdf-box"
                                  style={{ fontSize: 48, color: '#dc2626' }}
                                  aria-hidden
                                />
                                <span className={styles.editPdfFileName}>
                                  Documento atual enviado
                                </span>
                                <a
                                  href={existingCertificatePdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.editPdfViewButton}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icon
                                    icon="mdi:open-in-new"
                                    style={{ fontSize: 16 }}
                                    aria-hidden
                                  />
                                  Visualizar PDF
                                </a>
                              </div>
                            ) : (
                              <>
                                <Icon
                                  icon="mdi:file-pdf-box-outline"
                                  style={{
                                    fontSize: 28,
                                    color: 'var(--color-text-muted)',
                                  }}
                                  aria-hidden
                                />
                                <span className={styles.editUploadHint}>
                                  PDF, até 10MB
                                </span>
                                <span className={styles.editUploadButton}>
                                  Escolher arquivo
                                </span>
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {form.regulation_content_type === 'document' && (
                    <div className={styles.editSubsection}>
                      <h3 className={styles.editSubsectionTitle}>Dados do certificado</h3>
                      <div className={styles.editGridWide}>
                        <div className={styles.editField}>
                          <label
                            className={styles.editLabel}
                            htmlFor="campaign-certificate-number"
                          >
                            Número do certificado
                            <span className={styles.editLabelHint}>
                              (Número oficial usado no e-mail e em materiais da campanha)
                            </span>
                          </label>
                          <input
                            id="campaign-certificate-number"
                            type="text"
                            className={styles.editInput}
                            maxLength={255}
                            placeholder="Ex: 12345/2024"
                            value={form.certificate_number ?? ''}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                certificate_number: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="numeros-premiados">
              {isDonationOnlyCampaign ? (
                <section className={styles.editSection}>
                  <p className={styles.editLabelHint}>
                    Números premiados não se aplicam a campanhas configuradas como compras
                    sem números da sorte.
                  </p>
                </section>
              ) : (
                <CampanhaPremiatedNumbersTab
                  entries={form.premiated_lucky_numbers ?? []}
                  maxDigitsPerNumber={maxPremiatedDigitsPerNumber}
                  assignments={form.premiated_number_assignments}
                  onEntriesChange={(next) =>
                    setForm((prev) => ({ ...prev, premiated_lucky_numbers: next }))
                  }
                />
              )}
            </ContainerTabPanel>

            <ContainerTabPanel value="email">
              <section className={styles.editSection} aria-labelledby="section-email">
                <h2 id="section-email" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:email-outline"
                    aria-hidden
                  />
                  E-mails da plataforma
                </h2>
                <div className={styles.editSectionBody}>
                  <p className={styles.editLabelHint}>
                    A Sortee envia as confirmações de compra, recuperação de acesso e
                    demais mensagens da conta usando o padrão da plataforma. Os detalhes
                    de cada compra continuam identificando a campanha correspondente.
                  </p>
                  <div className={styles.editField}>
                    <label
                      className={styles.editLabel}
                      htmlFor="campaign-login-url-public"
                    >
                      Link para acessar a participação na campanha
                    </label>
                    <input
                      id="campaign-login-url-public"
                      type="url"
                      className={styles.editInput}
                      placeholder="Ex: https://exemplo.com/login"
                      value={form.login_url ?? ''}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, login_url: event.target.value }))
                      }
                    />
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="pixels">
              <section className={styles.editSection} aria-labelledby="section-pixels">
                <h2 id="section-pixels" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:chart-timeline-variant"
                    aria-hidden
                  />
                  Acompanhamento de anúncios e compras
                </h2>
                <div className={styles.editSectionBody}>
                  <p className={styles.editLabelHint} style={{ marginBottom: '1.25rem' }}>
                    Conecte as plataformas usadas para acompanhar visitas e compras desta
                    campanha. Preencha apenas as integrações que utiliza. Deixar um campo
                    vazio desativa a configuração correspondente.
                  </p>

                  <h3 className={styles.editSectionSubtitle}>Meta (Facebook)</h3>
                  <div className={styles.editGrid2}>
                    <div className={styles.editField}>
                      <label className={styles.editLabel} htmlFor="campaign-fb-pixel">
                        ID do Pixel da Meta
                        <span className={styles.editLabelHint}>
                          Copie o identificador numérico do seu Pixel no Gerenciador de
                          Eventos da Meta.
                        </span>
                      </label>
                      <input
                        id="campaign-fb-pixel"
                        type="text"
                        className={styles.editInput}
                        placeholder="Ex.: 1234567890123456"
                        maxLength={255}
                        value={form.fb_pixel ?? ''}
                        onChange={(e) => {
                          setMarketingFieldErrors((prev) => {
                            const next = { ...prev }
                            delete next.fb_pixel
                            return next
                          })
                          setForm((prev) => ({ ...prev, fb_pixel: e.target.value }))
                        }}
                      />
                      {marketingFieldErrors.fb_pixel ? (
                        <p className={styles.editFieldError} role="alert">
                          {marketingFieldErrors.fb_pixel}
                        </p>
                      ) : null}
                    </div>
                    <div className={styles.editField}>
                      <label
                        className={styles.editLabel}
                        htmlFor="campaign-fb-access-token"
                      >
                        Token da API de Conversões (CAPI)
                        <span className={styles.editLabelHint}>
                          (necessário para enviar evento de compra pelo servidor)
                        </span>
                      </label>
                      <input
                        id="campaign-fb-access-token"
                        type="text"
                        className={styles.editInput}
                        placeholder="Cole o token de acesso da Conversions API"
                        autoComplete="off"
                        spellCheck={false}
                        value={form.fb_access_token ?? ''}
                        onChange={(e) => {
                          setMarketingFieldErrors((prev) => {
                            const next = { ...prev }
                            delete next.fb_access_token
                            return next
                          })
                          setForm((prev) => ({
                            ...prev,
                            fb_access_token: e.target.value,
                          }))
                        }}
                      />
                      {marketingFieldErrors.fb_access_token ? (
                        <p className={styles.editFieldError} role="alert">
                          {marketingFieldErrors.fb_access_token}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <h3
                    className={styles.editSectionSubtitle}
                    style={{ marginTop: '1.75rem' }}
                  >
                    Google (GA4 e Tag Manager)
                  </h3>
                  <p className={styles.editLabelHint} style={{ marginBottom: '1rem' }}>
                    Use o contêiner GTM (GTM-…) para carregar tags no navegador ou o ID de
                    medição GA4 (G-…) direto, sem os dois ao mesmo tempo, para evitar
                    contagens duplicadas de página. Para enviar compras pelo Measurement
                    Protocol, informe o ID G- e o segredo gerado no GA4.
                  </p>
                  <div className={styles.editGrid2}>
                    <div className={styles.editField}>
                      <label
                        className={styles.editLabel}
                        htmlFor="campaign-gtm-container"
                      >
                        Google Tag Manager
                        <span className={styles.editLabelHint}>
                          (contêiner GTM-XXXXXX, opcional)
                        </span>
                      </label>
                      <input
                        id="campaign-gtm-container"
                        type="text"
                        className={styles.editInput}
                        placeholder="Ex.: GTM-AB12CDE"
                        maxLength={255}
                        value={form.g_pixel ?? ''}
                        onChange={(e) => {
                          setMarketingFieldErrors((prev) => {
                            const next = { ...prev }
                            delete next.g_pixel
                            return next
                          })
                          setForm((prev) => ({ ...prev, g_pixel: e.target.value }))
                        }}
                      />
                      {marketingFieldErrors.g_pixel ? (
                        <p className={styles.editFieldError} role="alert">
                          {marketingFieldErrors.g_pixel}
                        </p>
                      ) : null}
                    </div>
                    <div className={styles.editField}>
                      <label
                        className={styles.editLabel}
                        htmlFor="campaign-ga4-measurement-id"
                      >
                        ID de medição GA4
                        <span className={styles.editLabelHint}>
                          Formato G-XXXXXXXXXX. Necessário junto do segredo abaixo para
                          registrar compras confirmadas no Google.
                        </span>
                      </label>
                      <input
                        id="campaign-ga4-measurement-id"
                        type="text"
                        className={styles.editInput}
                        placeholder="Ex.: G-XXXXXXXXXX"
                        maxLength={50}
                        value={form.gtm_measurement_id ?? ''}
                        onChange={(e) => {
                          setMarketingFieldErrors((prev) => {
                            const next = { ...prev }
                            delete next.gtm_measurement_id
                            return next
                          })
                          setForm((prev) => ({
                            ...prev,
                            gtm_measurement_id: e.target.value,
                          }))
                        }}
                      />
                      {marketingFieldErrors.gtm_measurement_id ? (
                        <p className={styles.editFieldError} role="alert">
                          {marketingFieldErrors.gtm_measurement_id}
                        </p>
                      ) : null}
                    </div>
                    <div className={styles.editField} style={{ gridColumn: '1 / -1' }}>
                      <label
                        className={styles.editLabel}
                        htmlFor="campaign-ga4-mp-secret"
                      >
                        Segredo do Measurement Protocol (GA4)
                        <span className={styles.editLabelHint}>
                          (Administrador do GA4 {'>'} Transmitir dados {'>'} Measurement
                          Protocol — necessário junto do G- para enviar compras
                          confirmadas)
                        </span>
                      </label>
                      <input
                        id="campaign-ga4-mp-secret"
                        type="text"
                        className={styles.editInput}
                        placeholder="Cole o segredo da API"
                        autoComplete="off"
                        spellCheck={false}
                        maxLength={128}
                        value={form.ga4_measurement_protocol_secret ?? ''}
                        onChange={(e) => {
                          setMarketingFieldErrors((prev) => {
                            const next = { ...prev }
                            delete next.ga4_measurement_protocol_secret
                            return next
                          })
                          setForm((prev) => ({
                            ...prev,
                            ga4_measurement_protocol_secret: e.target.value,
                          }))
                        }}
                      />
                      {marketingFieldErrors.ga4_measurement_protocol_secret ? (
                        <p className={styles.editFieldError} role="alert">
                          {marketingFieldErrors.ga4_measurement_protocol_secret}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </section>
            </ContainerTabPanel>
          </form>
          {campaignId !== null && (
            <ContainerTabPanel value="combos">
              <section className={styles.editSection} aria-labelledby="section-combos">
                <h2 id="section-combos" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:package-variant"
                    aria-hidden
                  />
                  Combos Promocionais
                </h2>
                <div className={styles.editSectionBody}>
                  <CombosManager
                    campaignId={campaignId}
                    campaignPrice={form.price}
                    widgetSnippetFields={{
                      widget_embed_button_text: form.widget_embed_button_text ?? '',
                      widget_embed_button_color: form.widget_embed_button_color ?? '',
                      widget_embed_button_text_color:
                        form.widget_embed_button_text_color ?? '',
                      widget_embed_button_radius_px:
                        form.widget_embed_button_radius_px ?? '',
                    }}
                    landingButtonColor={form.button_color ?? ''}
                  />
                </div>
              </section>
            </ContainerTabPanel>
          )}

          {campaignId !== null && (
            <CampaignEditorPersistentPanel
              key={`roulette-${campaignId}`}
              value="roleta"
              active={activeTab === 'roleta'}
            >
              <section className={styles.editSection} aria-labelledby="section-roleta">
                <h2 id="section-roleta" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:circle-slice-8"
                    aria-hidden
                  />
                  Roleta da Sorte
                </h2>
                <div className={styles.editSectionBody}>
                  <RoletaPanel
                    campaignId={campaignId}
                    token={token}
                    onSaved={reportSaveSuccess}
                    onSaveStateChange={setRouletteSave}
                    savingCampaign={isSubmitting || isSavingAllPanels}
                  />
                </div>
              </section>
            </CampaignEditorPersistentPanel>
          )}

          {campaignId !== null && loadedCampaignId === campaignId && (
            <CampaignEditorPersistentPanel
              key={`api-roulette-${campaignId}`}
              value="roleta-api"
              active={activeTab === 'roleta-api'}
            >
              <ApiRoulettePanel
                campaignId={campaignId}
                onSaveStateChange={setApiRouletteSave}
                savingCampaign={isSubmitting || isSavingAllPanels}
              />
            </CampaignEditorPersistentPanel>
          )}

          {campaignId !== null && (
            <ContainerTabPanel value="pergunta">
              <section className={styles.editSection} aria-labelledby="section-pergunta">
                <h2 id="section-pergunta" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:help-circle-outline"
                    aria-hidden
                  />
                  Pergunta
                </h2>
                <div className={styles.editSectionBody}>
                  <PerguntasManager campaignId={campaignId} />
                </div>
              </section>
            </ContainerTabPanel>
          )}

          {campaignId !== null && (
            <ContainerTabPanel value="widget" keepMounted>
              <section className={styles.editSection} aria-labelledby="section-widget">
                <h2 id="section-widget" className={styles.editSectionTitle}>
                  <Icon
                    className={styles.editSectionTitleIcon}
                    icon="mdi:code-tags"
                    aria-hidden
                  />
                  Widget de Compra Externo
                </h2>
                <div className={styles.editSectionBody}>
                  <CampaignWidgetManager
                    campaignId={campaignId}
                    widgetEmbed={{
                      widget_embed_button_text: form.widget_embed_button_text ?? '',
                      widget_embed_button_color: form.widget_embed_button_color ?? '',
                      widget_embed_button_text_color:
                        form.widget_embed_button_text_color ?? '',
                      widget_embed_button_radius_px:
                        form.widget_embed_button_radius_px ?? '',
                    }}
                    landingButtonColor={form.button_color ?? ''}
                    onWidgetEmbedChange={(patch) =>
                      setForm((prev) => ({ ...prev, ...patch }))
                    }
                    liveCampaignFields={{
                      price: form.price ?? '',
                      min_quantity_per_purchase: form.min_quantity_per_purchase ?? '',
                      max_quantity_per_purchase: form.max_quantity_per_purchase ?? '',
                    }}
                  />
                </div>
              </section>
            </ContainerTabPanel>
          )}

          <CampaignEditorPersistentPanel
            key={campaignId}
            value="gamificacao"
            active={activeTab === 'gamificacao'}
          >
            <GamificacaoTab
              campaignId={campaignId}
              onSaveStateChange={setGamificationSave}
              onSaved={reportSaveSuccess}
            />
          </CampaignEditorPersistentPanel>
          <CampaignEditorPersistentPanel
            key={`purchase-scratch-${campaignId}`}
            value="raspadinha-compra"
            active={activeTab === 'raspadinha-compra'}
          >
            <PurchaseScratchTab
              campaignId={campaignId}
              onSaveStateChange={setPurchaseScratchSave}
              onSaved={reportSaveSuccess}
            />
          </CampaignEditorPersistentPanel>
          <CampaignEditorPersistentPanel
            key={`ai-${campaignId}`}
            value="inteligencia-artificial"
            active={activeTab === 'inteligencia-artificial'}
          >
            <CampaignAiTab
              campaignId={campaignId}
              onSaveStateChange={setAiSave}
              onSaved={reportSaveSuccess}
            />
          </CampaignEditorPersistentPanel>
          <CampaignEditorPersistentPanel
            key={`avatar-${campaignId}`}
            value="avatar"
            active={activeTab === 'avatar'}
          >
            <Suspense fallback={<p role="status">Abrindo ateliê de avatar…</p>}>
              <AvatarTab
                campaignId={campaignId}
                onSaveStateChange={setAvatarSave}
                onSaved={reportSaveSuccess}
              />
            </Suspense>
          </CampaignEditorPersistentPanel>
        </ContainerTabs>
      )}
      {template3MissingModalOpen ? (
        <Template3MissingContentModal
          form={form}
          isSubmitting={isSubmitting}
          serverError={submitError}
          onClose={handleTemplate3MissingClose}
          onSave={handleTemplate3MissingSave}
          imageAccept={CAMPAIGN_IMAGE_ACCEPT}
          validateFileSize={validateFileSize}
          maxImageBytes={MAX_IMAGE_BYTES}
        />
      ) : null}
      {sellerIdChangeModalOpen && (
        <SensitiveSellerIdModal
          confirmChange
          nextId={pendingSellerChangeRef.current?.doey_seller_id ?? ''}
          isSubmitting={isSubmitting}
          errorMessage={submitError}
          onCancel={() => {
            pendingSellerChangeRef.current = null
            setSellerIdChangeModalOpen(false)
          }}
          onSubmit={(code) => {
            const pending = pendingSellerChangeRef.current
            if (!pending) return
            void doSave({ ...pending, seller_id_totp_code: code }).then((saved) => {
              if (saved) {
                pendingSellerChangeRef.current = null
                setSellerIdChangeModalOpen(false)
                reportSaveSuccess()
              }
            })
          }}
        />
      )}
    </div>
  )
}
